import { useMutation } from '@tanstack/react-query';
import { apiClient, isNetworkError } from '@/lib/api';
import toast from '@/lib/toast';
import type { GenerateDraftRequest, GenerateDraftResponse } from '@/lib/types';

export function useGenerateDraft() {
  return useMutation({
    mutationFn: (request: GenerateDraftRequest) =>
      apiClient.generateDraft(request),
    retry: (failureCount, error) => {
      // Don't retry on auth errors (401, 403) or validation errors (400)
      if (error instanceof Error && (error.message.includes('401') || error.message.includes('403') || error.message.includes('400'))) {
        return false;
      }
      // Retry up to 2 times for network errors and 5xx errors
      if (isNetworkError(error) || (error instanceof Error && error.message.includes('500'))) {
        return failureCount < 2;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    onSuccess: (data: GenerateDraftResponse) => {
      console.log('Draft generated:', data);

      // Show warning if confidence is low
      if (data.confidence < 0.7) {
        toast.warning('Draft generated with low confidence', {
          description: 'Please review carefully before sending.',
        });
      } else {
        toast.success('Draft generated', {
          description: 'Your draft is ready to review.',
        });
      }
    },
    onError: (error: Error) => {
      console.error('Draft generation failed:', error);
      toast.error('Failed to generate draft', {
        description: error.message || 'Please try again.',
      });
    },
  });
}

export function useRegenerateDraft() {
  return useMutation({
    mutationFn: ({ draftId, options }: { draftId: string; options: any }) =>
      apiClient.regenerateDraft(draftId, options),
    retry: (failureCount, error) => {
      // Don't retry on auth errors or validation errors
      if (error instanceof Error && (error.message.includes('401') || error.message.includes('403') || error.message.includes('400'))) {
        return false;
      }
      // Retry up to 2 times for network errors and 5xx errors
      if (isNetworkError(error) || (error instanceof Error && error.message.includes('500'))) {
        return failureCount < 2;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    onSuccess: (data: GenerateDraftResponse) => {
      console.log('Draft regenerated:', data);
      toast.success('Draft regenerated', {
        description: 'Your updated draft is ready.',
      });
    },
    onError: (error: Error) => {
      console.error('Draft regeneration failed:', error);
      toast.error('Failed to regenerate draft', {
        description: error.message || 'Please try again.',
      });
    },
  });
}
